import React, { useEffect, useState } from 'react';

import { createStyles, Group, Text, Title } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import useWorkoutHistory from 'hooks/workoutHistory/useWorkoutHistory';
import { AthleteRetrieve, WorkoutHistoryRow } from 'types';
import { MOVEMENT_TYPE_FILTER_ALL } from 'types/enums';
import { formatDateForApi, formatDateForDisplay } from 'utils/date-functions';

import MovementHistoryDetail from './components/MovementHistoryDetail';
import WorkoutHistoryFilters from './components/WorkoutHistoryFilters';
import WorkoutHistoryTable from './components/WorkoutHistoryTable';
import { WorkoutHistorySearchFilters } from './types';

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xs,
  },
}));

const WorkoutTracker = ({ athlete }: { athlete: AthleteRetrieve }): JSX.Element => {
  const { classes } = useStyles();
  const { trackWorkoutHistoryViewed, trackWorkoutHistoryMovementOpened } = useStaffTracking();

  // The shared Tabs mounts only the active panel, so this fires on opening the tab and
  // not on every athlete profile.
  useEffect(() => {
    trackWorkoutHistoryViewed();
  }, [trackWorkoutHistoryViewed]);

  const initialFilterValues: WorkoutHistorySearchFilters = {
    search: '',
    type: MOVEMENT_TYPE_FILTER_ALL,
    dateRange: [null, null],
  };

  const [detailRow, setDetailRow] = useState<WorkoutHistoryRow | null>(null);

  // Filters and sort are held here, not in the table, so both survive a trip into a
  // detail and back — the table unmounts while a detail is open.
  const [searchFilters, setSearchFilters] = useState<WorkoutHistorySearchFilters>(initialFilterValues);
  const [debouncedSearchFilters] = useDebouncedValue<WorkoutHistorySearchFilters>(searchFilters, 250);
  const [sortColumn, setSortColumn] = useState('latestDate');
  const [ascending, setAscending] = useState(false);

  const handleHeaderClick = (column: string): void => {
    setSortColumn(column);
    setAscending(a => !a);
  };

  // Only a row with a movement id is openable, so the id is always present here.
  const openDetail = (row: WorkoutHistoryRow): void => {
    setDetailRow(row);
    trackWorkoutHistoryMovementOpened({
      workout_movement_id: row.workoutMovementId ?? '',
      score_type: row.scoreType,
    });
  };

  // The athlete's most recent result overall, so it is read unfiltered. Its count is
  // also what lets the table tell a never-logged athlete from filtered-out rows.
  const { data: lastActivityData } = useWorkoutHistory({
    athleteId: athlete.athleteId,
    limit: '1',
  });
  const lastActivity = lastActivityData?.pages?.[0]?.data?.results?.[0]?.latestDate;
  const totalResults = lastActivityData?.pages?.[0]?.data?.count;

  if (detailRow) {
    return (
      <MovementHistoryDetail athleteId={athlete.athleteId} row={detailRow} back={() => setDetailRow(null)} />
    );
  }

  return (
    <>
      <div className={classes.header}>
        <Group position='apart' align='flex-start'>
          <div>
            <Title order={2}>Historical Performance</Title>
            <Text color='dimmed' size='sm'>
              Athlete&apos;s workout performance, History and Best results
            </Text>
          </div>
          <Text color='dimmed' size='sm'>
            Last activity: {lastActivity ? formatDateForDisplay(lastActivity) : '—'}
          </Text>
        </Group>
      </div>

      <WorkoutHistoryFilters
        searchFilters={searchFilters}
        initialFilterValues={initialFilterValues}
        setSearchFilters={setSearchFilters}
      />

      <Text color='dimmed' size='xs' px='xl' pb='xs'>
        Each row is one movement or benchmark under one score type, so the same movement can appear more than
        once. The date range filters which rows appear. A row shows if any of its results fall in range, and
        Last Result and Best Result always reflect the athlete&apos;s full history.
      </Text>

      <WorkoutHistoryTable
        athleteId={athlete.athleteId}
        openDetail={openDetail}
        sortColumn={sortColumn}
        ascending={ascending}
        handleHeaderClick={handleHeaderClick}
        totalResults={totalResults}
        searchFilters={{
          search: debouncedSearchFilters.search,
          type: debouncedSearchFilters.type,
          startDateRange: formatDateForApi(debouncedSearchFilters.dateRange[0]),
          endDateRange: formatDateForApi(debouncedSearchFilters.dateRange[1]),
        }}
      />
    </>
  );
};

export default WorkoutTracker;
